import { useTranslation } from 'react-i18next'
import { find, propEq, equals } from 'ramda'
import { useState } from 'react'
import { Manager, Reference, Popper } from 'react-popper'
import styled from 'styled-components'
import { CloseClickOutside } from '@/components/window'
import { useFlag } from '@/flags'
import { colors, fontWeights } from '@/styles'
import arrowIcon from './down-arrow-white.png'

const Box = styled.div`
  height: 30px;
  padding: 0 10px;
  outline: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  position: relative;
  color: ${colors.white};
  font-size: 14px;
`

const Icon = styled.img<{ opened: boolean }>`
  margin-left: 10px;
  width: 12px;
  transition: all 0.3s linear;
  transform: rotate(0deg);

  ${({ opened }) => (opened ? 'transform: rotate(180deg);' : '')}
`

const Dropdown = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  width: 148px;
  border: 1px solid ${colors.grey};
  border-radius: 0 0 3px 3px;
  cursor: pointer;
  background-color: ${colors.white};
  z-index: 1;
`

const OptionItem = styled.a<{ active: boolean }>`
  color: ${colors.darkGrey};
  font-size: 11px;
  letter-spacing: 0.25px;
  font-weight: ${fontWeights.medium};
  text-align: center;
  text-transform: uppercase;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  border-bottom: 1px solid ${colors.darkGrey};

  &:hover {
    background: ${colors.grey};
  }

  &:last-child {
    border-bottom: none;
  }

  ${props =>
    props.active &&
    `
    opacity: 0.3;
    cursor: default;
  `}
`

const LANGUAGES = [
  {
    value: 'en',
    label: 'Canada (EN)'
  },
  {
    value: 'zh',
    label: '中文 (CN)'
  }
]

const LANGUAGES_WITH_FRENCH = [
  {
    value: 'en',
    label: 'Canada (EN)'
  },
  {
    value: 'fr',
    label: 'Canada (FR)'
  },
  {
    value: 'zh',
    label: '中文 (CN)'
  }
]

const LanguageSelector = () => {
  const { i18n } = useTranslation()
  const [opened, setOpened] = useState(false)
  const showFrench = useFlag(['features', 'translations', 'french'], false)
  const languages = showFrench ? LANGUAGES_WITH_FRENCH : LANGUAGES
  const currentLang = i18n.language
  const language: Record<string, string> | undefined =
    find(propEq('value', currentLang.substring(0, 2)))(languages) || undefined

  const toggle = () => setOpened(!opened)
  const close = () => setOpened(false)
  const onLanguageChange = (lang: string) => i18n.changeLanguage(lang)

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <Box onClick={toggle} ref={ref}>
            {language?.label}
            <Icon opened={opened} src={arrowIcon} alt="Toggle Dropdown" />
          </Box>
        )}
      </Reference>
      <Popper placement="top-end">
        {({ ref, style, placement }) =>
          opened && (
            <CloseClickOutside onClickOutside={close}>
              <Dropdown style={style} ref={ref} data-placement={placement}>
                {languages.map(option => (
                  <OptionItem
                    onClick={() => onLanguageChange(option.value)}
                    active={equals(option, language)}
                    key={option.value}
                  >
                    {option.label}
                  </OptionItem>
                ))}
              </Dropdown>
            </CloseClickOutside>
          )
        }
      </Popper>
    </Manager>
  )
}

export default LanguageSelector
